import React from "react";
import {
  PaperClipIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { PersonDocument } from "../api/360Files";
import { classNames } from "../utils";

import LoadingSpinner from "../shared-components/LoadingSpinner";

const NOT_PROVIDED = "Not Provided";

export interface DocsFormSectionProps {
  docs: PersonDocument[];
  handleDelete?: (doc: PersonDocument) => void;
  handleAdd?: () => void;
  isDeleting?: boolean;
  title: string;
  enableDelete?: boolean;
  enableAdd?: boolean;
  isLoadingDocs?: boolean;
  docsLoadError?: boolean;
  onRetryLoadDocs?: () => void;
}

const DocsFormSection: React.FC<DocsFormSectionProps> = ({
  docs,
  handleDelete,
  handleAdd,
  isDeleting = false,
  title,
  enableDelete = false,
  enableAdd = false,
  isLoadingDocs = false,
  docsLoadError = false,
  onRetryLoadDocs,
}) => {
  const hasItems = docs.length > 0;
  const showDocs = !isLoadingDocs && !docsLoadError;

  return (
    <div className={classNames("mb-3 w-full", "text-sm")}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={"text-sm text-gray-500"}>{title}</span>

        {handleAdd && enableAdd && !isLoadingDocs && (
          <button type="button" onClick={handleAdd}>
            <PlusCircleIcon className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>
      <div className="relative">
        {(isLoadingDocs || isDeleting) && (
          <div className="flex items-center py-1">
            <LoadingSpinner className="w-6 h-6" />
          </div>
        )}
        {docsLoadError && !isLoadingDocs && (
          <div className="text-sm text-gray-500">
            <span>Unable to load documents.</span>{" "}
            {onRetryLoadDocs && (
              <button
                type="button"
                className="text-red-600 hover:underline"
                onClick={onRetryLoadDocs}
              >
                Retry
              </button>
            )}
          </div>
        )}
        {!hasItems && !isDeleting && !isLoadingDocs && !docsLoadError && (
          <span className={"block text-sm leading-5 text-gray-500"}>
            {NOT_PROVIDED}
          </span>
        )}
        {showDocs &&
          docs.length > 0 &&
          docs.map((doc) => (
            <div
              key={doc.url}
              className="flex gap-2 items-center mt-0.5 min-w-0 cursor-pointer select-none"
              onClick={() => window.open(doc.url, "_blank")}
            >
              <PaperClipIcon className="w-4 h-4 flex-shrink-0 text-gray-500" />
              <span
                className="min-w-0 flex-1 truncate text-sm font-semibold leading-5 text-gray-700"
                title={doc.filename}
              >
                {doc.filename}
              </span>
              {handleDelete && enableDelete && (
                <button
                  type="button"
                  className="p-0.5 -m-0.5 flex-shrink-0 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                  disabled={isDeleting || !enableDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(doc);
                  }}
                  title={`Remove ${title}`}
                  aria-label={`Remove ${title}`}
                >
                  <XMarkIcon className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default DocsFormSection;
